import React from 'react';
import { Spin, Empty } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { ecrireExcel, ajouterEntete, COL_WIDTHS_FINANCIER, nomFichier } from './excelUtils';
import { TableauSinistres, C } from './TableauReporting';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { libellesTraduits } from '../../../i18n/libelles';

// MOIS_FR[1..12] : nom du mois dans la langue de l'interface, lu à l'usage.
const MOIS_FR = libellesTraduits('statistiques:commun.mois');
const tx = (cle, options) => i18n.t(`statistiques:reporting.${cle}`, options);

const SectionHeader = ({ title, note }) => (
  <div style={{ background: '#1e293b', color: '#fff', padding: '10px 16px',
    borderRadius: '10px 10px 0 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontWeight: 700, fontSize: 13 }}>{title}</span>
    {note && <span style={{ fontSize: 11, opacity: 0.6 }}>{note}</span>}
  </div>
);

export function exportSinistres(data) {
  if (!data) return;
  const m = MOIS_FR[data.mois].toUpperCase();
  const n = data.annee, n1 = data.anneeRef;
  const moisAnnee = `${MOIS_FR[data.mois]} ${n}`;
  const nbCols = 18;

  const toAoa = (lignes, total, labelFn) => {
    const h1 = ['', tx('excel.moisDe', { mois: m }),'','','','','','','',
                 tx('excel.janvierA', { mois: m }),'','','','','','',tx('excel.finAnnee')];
    const h2 = ['','TE','','ET','','TOTAL','',tx('excel.var'),'%',
                 'TE','','ET','','TOTAL','',tx('excel.var'),'%','TE+ET'];
    const h3 = ['',n1,n,n1,n,n1,n,`${n}/${n1}`,n,n1,n,n1,n,n1,n,`${n}/${n1}`,n,''];
    const rows = lignes.map(l => [
      labelFn(l),
      l.te_mois_n1,l.te_mois_n,l.et_mois_n1,l.et_mois_n,
      l.tot_mois_n1,l.tot_mois_n,
      l.tot_mois_n1?(l.tot_mois_n-l.tot_mois_n1)/l.tot_mois_n1:'',
      total.tot_mois_n?l.tot_mois_n/total.tot_mois_n:'',
      l.te_cumul_n1,l.te_cumul_n,l.et_cumul_n1,l.et_cumul_n,
      l.tot_cumul_n1,l.tot_cumul_n,
      l.tot_cumul_n1?(l.tot_cumul_n-l.tot_cumul_n1)/l.tot_cumul_n1:'',
      total.tot_cumul_n?l.tot_cumul_n/total.tot_cumul_n:'',
      l.tot_fda_n,
    ]);
    const t = total;
    rows.push(['TOTAL',t.te_mois_n1,t.te_mois_n,t.et_mois_n1,t.et_mois_n,
      t.tot_mois_n1,t.tot_mois_n,'','',
      t.te_cumul_n1,t.te_cumul_n,t.et_cumul_n1,t.et_cumul_n,
      t.tot_cumul_n1,t.tot_cumul_n,'','',t.tot_fda_n]);
    return [h1,h2,h3,...rows];
  };

  const buildSheet = (titre, lignes, total, labelFn) => {
    const aoaData = toAoa(lignes, total, labelFn);
    const { aoa, merges } = ajouterEntete(
      aoaData, tx('excel.entete.sinistres'), titre, moisAnnee, nbCols
    );
    return {
      name: titre.substring(0, 31),
      aoa,
      colWidths: [18,6,6,6,6,7,7,8,7,6,6,6,6,7,7,8,7,8],
      rowHeights: [{hpt:16},{hpt:20},{hpt:16},{hpt:12},{hpt:28},{hpt:20},{hpt:16}],
      merges,
    };
  };

  ecrireExcel([
    buildSheet(tx('excel.feuilles.parPays'), data.parPays, data.totalPays, l=>l.pays),
    buildSheet(tx('excel.feuilles.parCompagnie'), data.parCompagnie, data.totalCompagnie, l=>l.compagnie),
  ], `Reporting_Sinistres_${MOIS_FR[data.mois]}_${n}`);
  toast.success(tx('excel.succes.sinistres'));
}

export default function TabSinistres({ data, loading }) {
  const { t } = useTranslation('statistiques');
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:60 }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize:28, color:C.green }} />} />
    </div>
  );
  if (!data) return <Empty description={t('commun.aucuneDonnee')} style={{ padding:40 }} />;

  const moisL = MOIS_FR[data.mois].toUpperCase();

  return (
    <div>
      {/* Tableau I */}
      <section style={{ marginBottom: 32 }}>
        <SectionHeader
          title={t('reporting.sections.sinistresParPays')}
          note={t('reporting.sections.donneesAu', { mois: MOIS_FR[data.mois], annee: data.annee })}
        />
        <TableauSinistres
          lignes={data.parPays}
          total={data.totalPays}
          getLabel={l => l.pays}
          moisLibelle={moisL}
          annee={data.annee}
          anneeRef={data.anneeRef}
        />
      </section>

      {/* Tableau II */}
      <section>
        <SectionHeader
          title={t('reporting.sections.statistiquesCima')}
          note={t('reporting.sections.compagniesMembresTogo')}
        />
        <TableauSinistres
          lignes={[...data.parCompagnie].sort((a,b) => {
            if (a.compagnie==='AUTRES') return 1;
            if (b.compagnie==='AUTRES') return -1;
            return a.compagnie.localeCompare(b.compagnie);
          })}
          total={data.totalCompagnie}
          getLabel={l => l.compagnie}
          moisLibelle={moisL}
          annee={data.annee}
          anneeRef={data.anneeRef}
        />
      </section>
    </div>
  );
}
